import PropTypes from 'prop-types';
import { memo } from 'react';
import { trans } from '@spotahome/soyuz/client';

import getStructuredDataAttributes from '@spotahome/marketplace-common/src/LEGACY/javascripts/microdata/lib/StructuredDataAttributes';

import { PhotoListShape } from '@spotahome/marketplace-common/src/shapes/listing';

import { ListingFormatter } from '@spotahome/ui-library/src/tenant/utils/images';

import { generatePositiveNotes } from '../utils/generatePositiveNotes';

const MAX_NUMBER_OF_PHOTOS = 10;
const getRealEstatAgentNode = () => {
  const { name, priceRange } = getStructuredDataAttributes();

  return {
    '@type': 'RealEstateAgent',
    name,
    image: '',
    address: '',
    telephone: '',
    priceRange
  };
};

const generatePhotoSd = photos =>
  photos.slice(0, MAX_NUMBER_OF_PHOTOS).map(photo => {
    return {
      '@type': 'ImageObject',
      url: photo
    };
  });

const JsonLdProduct = ({
  listingId,
  photoList,
  name = '',
  description = '',
  url = '',
  isRoomsAggregator = false,
  priceCurrency = 'EUR',
  price = 0,
  lowPrice = 0,
  highPrice = 0,
  offerCount = 0,
  reviews = {},
  title = '',
  review = '',
  features = []
}) => {
  const getJsonString = () => {
    const firstPhoto = photoList[0];
    const image = firstPhoto
      ? ListingFormatter.buildPhotoUrl(firstPhoto.id, firstPhoto.verified)
      : '';
    const thumbnailUrl = firstPhoto
      ? ListingFormatter.buildSquarePhotoUrl(firstPhoto.id)
      : '';
    const photos = photoList.length
      ? photoList.map(photo =>
          ListingFormatter.buildPhotoUrl(photo.id, photo.verified)
        )
      : [];
    const jsonSchemaObject = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name,
      image,
      description,
      url,
      brand: getRealEstatAgentNode(),
      sku: listingId
    };

    if (!isRoomsAggregator) {
      jsonSchemaObject.offers = {
        '@type': 'Offer',
        availability: 'https://schema.org/OnlineOnly',
        url,
        priceCurrency,
        price,
        itemOffered: {
          '@type': 'Residence'
        },
        seller: getRealEstatAgentNode()
      };
    } else {
      jsonSchemaObject.offers = {
        '@type': 'AggregateOffer',
        availability: 'https://schema.org/OnlineOnly',
        url,
        priceCurrency,
        lowPrice,
        highPrice,
        offerCount,
        itemOffered: {
          '@type': 'Residence'
        },
        seller: getRealEstatAgentNode()
      };
    }

    const ratingValue = parseInt(reviews?.ratingAverage, 10);
    const ratingCount = parseInt(reviews?.count, 10);

    if (ratingValue && ratingCount) {
      jsonSchemaObject.aggregateRating = {
        '@type': 'AggregateRating',
        itemReviewed: {
          '@type': 'Product',
          image,
          name
        },
        ratingValue,
        bestRating: 5,
        ratingCount
      };
    }

    jsonSchemaObject.review = {
      '@type': 'Review',
      name: 'Spotahome review',
      author: {
        '@type': 'Organization',
        name: 'Spotahome',
        url: 'https://spotahome.com/'
      },
      reviewBody: review,
      description: 'Review and Rating',
      headline: title,
      thumbnailUrl,
      publisher: {
        '@type': 'Organization',
        name: 'Spotahome',
        logo: {
          '@type': 'ImageObject',
          url: 'https://sah-public-assets.s3.eu-west-1.amazonaws.com/logo/logo_360.png',
          width: 360,
          height: 69
        }
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: 4,
        bestRating: 5,
        worstRating: 0
      },
      positiveNotes: {
        '@type': 'ItemList',
        itemListElement: generatePositiveNotes(features ?? {}, trans)
      },
      image: generatePhotoSd(photos)
    };

    return JSON.stringify(jsonSchemaObject);
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: getJsonString() }}
    />
  );
};

JsonLdProduct.propTypes = {
  listingId: PropTypes.string.isRequired,
  name: PropTypes.string,
  description: PropTypes.string,
  url: PropTypes.string,
  priceCurrency: PropTypes.string,
  price: PropTypes.number,
  isRoomsAggregator: PropTypes.bool,
  lowPrice: PropTypes.number,
  highPrice: PropTypes.number,
  offerCount: PropTypes.number,
  reviews: PropTypes.shape({
    count: PropTypes.number.isRequired,
    ratingAverage: PropTypes.number.isRequired
  }),
  title: PropTypes.string,
  review: PropTypes.string,
  photoList: PhotoListShape.isRequired,
  features: PropTypes.arrayOf(PropTypes.object)
};

export default memo(JsonLdProduct);
